import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { authenticatedApi } from '../../Api/api';
import DocumentViewer from '../DocumentViewer';
import { adaptToIDocument, AdaptedIDocument } from '../../utils/documentAdapter';
import env from '../../config/env';

interface FeeInvoice {
  id: number;
  label: string;
  documentId: number | null;
  amount: number;
  startDate: string | null;
  endDate: string | null;
  createdAt: string;
}

interface FeeInvoicesButtonProps {
  contractId: number;
  feeInvoiceIds: number[];
  className?: string;
}

/**
 * Button component for viewing fee invoices (factures de frais) linked to a contract
 * Opens a DocumentViewer modal with all fee invoices in list mode
 */
export default function FeeInvoicesButton({
  contractId,
  feeInvoiceIds,
  className = '',
}: FeeInvoicesButtonProps) {
  const [viewerOpen, setViewerOpen] = useState(false);
  const [documents, setDocuments] = useState<AdaptedIDocument[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<AdaptedIDocument | null>(null);
  const [loading, setLoading] = useState(false);

  const handleClick = async () => {
    if (feeInvoiceIds.length === 0) return;

    setLoading(true);
    try {
      // Fetch fee invoices details from the API
      const response = await authenticatedApi.get<{ invoices: FeeInvoice[] }>(
        `/contracts/${contractId}/fee-invoices`
      );

      const invoices = response.data.invoices;

      if (invoices.length === 0) {
        alert('Aucune facture de frais disponible pour ce contrat.');
        return;
      }

      // Adapt each invoice to IDocument format
      const adaptedDocs = invoices
        .filter(invoice => invoice.documentId) // Only include invoices with documents
        .map((invoice) =>
          adaptToIDocument({
            id: invoice.id,
            label: invoice.label,
            date: invoice.createdAt ? new Date(invoice.createdAt) : undefined,
            getViewUrl: async () => {
              const tokenResponse = await authenticatedApi.get<{ documentToken: string }>(
                `/invoices/${invoice.id}/get-download-token`
              );
              const token = tokenResponse.data.documentToken;
              return `${env.PUBLIC_API_URL}/documents/${invoice.documentId}/download?downloadType=view&documentDownloadToken=${token}`;
            },
            getDownloadUrl: async () => {
              const tokenResponse = await authenticatedApi.get<{ documentToken: string }>(
                `/invoices/${invoice.id}/get-download-token`
              );
              const token = tokenResponse.data.documentToken;
              return `${env.PUBLIC_API_URL}/documents/${invoice.documentId}/download?downloadType=download&documentDownloadToken=${token}`;
            },
            metadata: {
              amount: invoice.amount,
              startDate: invoice.startDate,
              endDate: invoice.endDate,
            },
          })
        );

      if (adaptedDocs.length === 0) {
        alert('Aucun document PDF disponible pour les factures de frais de ce contrat.');
        return;
      }

      setDocuments(adaptedDocs);
      setSelectedDocument(adaptedDocs[0]);
      setViewerOpen(true);
    } catch (error) {
      console.error('Error fetching fee invoices:', error);
      alert('Erreur lors de la récupération des factures de frais');
    } finally {
      setLoading(false);
    }
  };

  // Custom render for invoice list items
  const renderListItem = (doc: AdaptedIDocument) => {
    const metadata = doc._metadata || {};
    const startDate = metadata.startDate ? new Date(metadata.startDate) : null;
    const endDate = metadata.endDate ? new Date(metadata.endDate) : null;

    return (
      <div className="tw-flex tw-flex-col tw-gap-1">
        <div className="tw-font-medium tw-text-sm">{doc.label}</div>
        {startDate && endDate && (
          <div className="tw-text-xs tw-text-gray-600">
            {startDate.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
            {' - '}
            {endDate.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}
          </div>
        )}
        {metadata.amount !== undefined && (
          <div className="tw-text-xs tw-text-gray-500">
            {Number(metadata.amount).toFixed(2)} €
          </div>
        )}
      </div>
    );
  };

  const isDisabled = feeInvoiceIds.length === 0 || loading;
  const count = feeInvoiceIds.length;

  return (
    <>
      <button
        onClick={handleClick}
        disabled={isDisabled}
        className={`
          tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded-lg
          tw-transition-colors
          ${
            isDisabled
              ? 'tw-bg-gray-100 tw-text-gray-400 tw-cursor-not-allowed'
              : 'tw-bg-affinicar-lighter tw-text-affinicar-secondary hover:tw-bg-affinicar-lighter/80 tw-cursor-pointer'
          }
          ${className}
        `}
      >
        {loading ? (
          <Loader2 size={14} className="tw-animate-spin" />
        ) : (
          <FileText size={14} />
        )}
        <span>{count > 0 ? `${count} facture${count > 1 ? 's' : ''}` : '-'}</span>
      </button>

      <DocumentViewer
        document={selectedDocument}
        documents={documents}
        isOpen={viewerOpen}
        onClose={() => setViewerOpen(false)}
        onNavigate={setSelectedDocument}
        title="Factures de frais"
        getTokenUrl={() => ''} // Not used for adapted documents
        listMode={true}
        renderListItem={renderListItem}
      />
    </>
  );
}
